// API service for XPLOD-GROUP database operations
// This handles all database interactions through Cloudflare Workers API

class ApiService {
  constructor() {
    this.baseUrl =
      process.env.REACT_APP_API_URL || "https://xplod-group-api.workers.dev";
  }

  // Helper method for making API requests
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const token = localStorage.getItem("adminToken");

    console.log("=== API REQUEST DEBUG ===");
    console.log("Endpoint:", endpoint);
    console.log("Method:", options.method || "GET");
    console.log("Token exists:", !!token);

    // Log the request body for POST/PUT requests
    if (options.body) {
      console.log("Request body:", options.body);
      console.log("Body type:", typeof options.body);

      try {
        const parsedBody = JSON.parse(options.body);
        console.log("Parsed body keys:", Object.keys(parsedBody));
        console.log("Body structure:", {
          name: parsedBody.name,
          category_id: parsedBody.category_id,
          brand_id: parsedBody.brand_id,
          images: parsedBody.images?.length || 0,
          features: parsedBody.features?.length || 0,
          technical_details: parsedBody.technical_details?.length || 0,
        });
      } catch (e) {
        console.log("Could not parse body as JSON:", e.message);
      }
    }

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const now = Math.floor(Date.now() / 1000);
        console.log("Token expires at:", new Date(payload.exp * 1000));
        console.log("Current time:", new Date());
        console.log("Token expired:", payload.exp < now);
      } catch (e) {
        console.log("Could not decode token:", e.message);
      }
    }

    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
      ...options,
    };

    console.log("Headers:", config.headers);
    console.log("=== END DEBUG ===");

    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        const errorText = await response.text();
        console.log("=== ERROR RESPONSE ===");
        console.log("Status:", response.status);
        console.log("Status Text:", response.statusText);
        console.log("Error response body:", errorText);
        console.log("=== END ERROR ===");

        // Try to parse error as JSON for more details
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(
            `API Error: ${response.status} - ${errorJson.error || errorText}`,
          );
        } catch (parseError) {
          throw new Error(
            `API Error: ${response.status} ${response.statusText} - ${errorText}`,
          );
        }
      }
      return await response.json();
    } catch (error) {
      console.error(`API Request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Mock responses for development
  getMockResponse(endpoint, options) {
    const mockData = this.getMockData();

    return new Promise((resolve) => {
      setTimeout(() => {
        if (endpoint === "/api/products") {
          if (options.method === "GET") {
            resolve({ success: true, data: mockData.products });
          } else if (options.method === "POST") {
            const newProduct = {
              id: mockData.products.length + 1,
              ...JSON.parse(options.body),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            mockData.products.push(newProduct);
            resolve({ success: true, data: newProduct });
          }
        } else if (endpoint.match(/\/api\/products\/\d+/)) {
          const productId = parseInt(endpoint.split("/").pop());
          const product = mockData.products.find((p) => p.id === productId);

          if (options.method === "GET") {
            resolve({ success: true, data: product || null });
          } else if (options.method === "PUT") {
            const updatedProduct = {
              ...product,
              ...JSON.parse(options.body),
              updated_at: new Date().toISOString(),
            };
            const index = mockData.products.findIndex(
              (p) => p.id === productId,
            );
            if (index !== -1) {
              mockData.products[index] = updatedProduct;
            }
            resolve({ success: true, data: updatedProduct });
          } else if (options.method === "DELETE") {
            mockData.products = mockData.products.filter(
              (p) => p.id !== productId,
            );
            resolve({ success: true, data: null });
          }
        } else if (endpoint === "/api/categories") {
          resolve({ success: true, data: mockData.categories });
        }
      }, 500); // Simulate network delay
    });
  }

  // Product API methods
  async getProducts(filters = {}) {
    const params = new URLSearchParams();

    if (filters.category) params.append("category", filters.category);
    if (filters.subcategory_id) params.append("subcategory_id", filters.subcategory_id);
    if (filters.status) params.append("status", filters.status);
    if (filters.search) params.append("search", filters.search);
    if (filters.featured) params.append("featured", filters.featured);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.offset) params.append("offset", filters.offset);

    const queryString = params.toString();
    const endpoint = `/api/products${queryString ? `?${queryString}` : ""}`;

    return this.request(endpoint);
  }

  async getProductSubcategories(productId = null) {
    const params = new URLSearchParams();
    if (productId) params.append("product_id", productId);
    
    const queryString = params.toString();
    const endpoint = `/api/product-subcategories${queryString ? `?${queryString}` : ""}`;
    
    return this.request(endpoint);
  }

  async createProductSubcategory(productSubcategoryData) {
    return this.request("/api/product-subcategories", {
      method: "POST",
      body: JSON.stringify(productSubcategoryData),
    });
  }

  async deleteProductSubcategory(id) {
    return this.request(`/api/product-subcategories/${id}`, {
      method: "DELETE",
    });
  }

  async getProductsWithSubcategories(filters = {}) {
    const params = new URLSearchParams();

    if (filters.category) params.append("category", filters.category);
    if (filters.subcategory_id) params.append("subcategory_id", filters.subcategory_id);
    if (filters.status) params.append("status", filters.status);
    if (filters.search) params.append("search", filters.search);
    if (filters.featured) params.append("featured", filters.featured);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.offset) params.append("offset", filters.offset);
    // Add include parameter to get product_subcategories
    params.append("include", "product_subcategories");

    const queryString = params.toString();
    const endpoint = `/api/products${queryString ? `?${queryString}` : ""}`;

    return this.request(endpoint);
  }

  async getProduct(id) {
    return this.request(`/api/products/${id}`);
  }

  async getProductBySlug(slug) {
    return this.request(`/api/products/slug/${slug}`);
  }

  async createProduct(productData) {
    console.log("Creating product with data:", productData);
    console.log("Images being sent:", productData.images);
    return this.request("/api/products", {
      method: "POST",
      body: JSON.stringify(productData),
    });
  }

  // Create product with image upload (FormData)
  async createProductWithImages(formData) {
    const url = `${this.baseUrl}/api/products`;
    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData, // FormData automatically sets correct Content-Type
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Product upload failed:", error);
      if (this.isDevelopment) {
        // Mock successful upload for development
        return new Promise((resolve) => {
          setTimeout(() => {
            const newProduct = {
              id: Date.now(),
              name: formData.get("name") || "New Product",
              sku: formData.get("sku") || "SKU" + Date.now(),
              description: formData.get("description") || "",
              price: parseFloat(formData.get("price")) || 0,
              category: formData.get("category") || "General",
              status: "active",
              stock_quantity: parseInt(formData.get("inventory_quantity")) || 0,
              images: [
                {
                  id: 1,
                  image_url: "https://via.placeholder.com/400x300",
                  alt_text: formData.get("name") || "Product Image",
                  is_primary: true,
                },
              ],
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            resolve({ success: true, data: newProduct });
          }, 1000);
        });
      }
      throw error;
    }
  }

  async updateProduct(id, productData) {
    console.log("Updating product with data:", productData);
    console.log("Images being sent:", productData.images);
    return this.request(`/api/products/${id}`, {
      method: "PUT",
      body: JSON.stringify(productData),
    });
  }

  // Update product with image upload (FormData)
  async updateProductWithImages(id, formData) {
    const url = `${this.baseUrl}/api/products/${id}`;
    try {
      const response = await fetch(url, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Product update failed:", error);
      if (this.isDevelopment) {
        // Mock successful update for development
        return new Promise((resolve) => {
          setTimeout(() => {
            const updatedProduct = {
              id: parseInt(id),
              name: formData.get("name") || "Updated Product",
              sku: formData.get("sku") || "SKU" + id,
              description: formData.get("description") || "",
              price: parseFloat(formData.get("price")) || 0,
              category: formData.get("category") || "General",
              status: "active",
              stock_quantity: parseInt(formData.get("inventory_quantity")) || 0,
              images: [
                {
                  id: 1,
                  image_url: "https://via.placeholder.com/400x300",
                  alt_text: formData.get("name") || "Product Image",
                  is_primary: true,
                },
              ],
              updated_at: new Date().toISOString(),
            };
            resolve({ success: true, data: updatedProduct });
          }, 1000);
        });
      }
      throw error;
    }
  }

  async deleteProduct(id) {
    return this.request(`/api/products/${id}`, {
      method: "DELETE",
    });
  }

  async updateProductStock(id, quantity) {
    return this.request(`/api/products/${id}/stock`, {
      method: "PATCH",
      body: JSON.stringify({ stock_quantity: quantity }),
    });
  }

  // Category API methods
  // async getCategories() {
  //   return this.request("/api/categories");
  // }

  // async getCategory(id) {
  //   return this.request(`/api/categories/${id}`);
  // }

  // async getCategoryBySlug(slug) {
  //   return this.request(`/api/categories/slug/${slug}`);
  // }

  // ADD THESE NEW METHODS HERE:
  async createCategory(categoryData) {
    return this.request("/api/categories", {
      method: "POST",
      body: JSON.stringify(categoryData),
    });
  }

  // Brand API methods
  async getBrands() {
    return this.request("/api/brands");
  }

  async getBrand(id) {
    return this.request(`/api/brands/${id}`);
  }

  async createBrand(brandData) {
    return this.request("/api/brands", {
      method: "POST",
      body: JSON.stringify(brandData),
    });
  }

  async updateBrand(id, brandData) {
    return this.request(`/api/brands/${id}`, {
      method: "PUT",
      body: JSON.stringify(brandData),
    });
  }

  async deleteBrand(id) {
    return this.request(`/api/brands/${id}`, {
      method: "DELETE",
    });
  }

  // Type API methods
  async getTypes() {
    return this.request("/api/types");
  }

  async getType(id) {
    return this.request(`/api/types/${id}`);
  }

  async createType(typeData) {
    return this.request("/api/types", {
      method: "POST",
      body: JSON.stringify(typeData),
    });
  }

  async updateType(id, typeData) {
    return this.request(`/api/types/${id}`, {
      method: "PUT",
      body: JSON.stringify(typeData),
    });
  }

  async deleteType(id) {
    return this.request(`/api/types/${id}`, {
      method: "DELETE",
    });
  }

  // Category API methods
  async getCategories() {
    return this.request("/api/categories");
  }

  async getCategory(id) {
    return this.request(`/api/categories/${id}`);
  }

  async getCategoryBySlug(slug) {
    return this.request(`/api/categories/slug/${slug}`);
  }

  // Add this in the ApiService class, after getCategoryBySlug

  async getCategoryCounts() {
    return this.request("/api/categories/counts");
  }

  // Subcategory API methods
  async getSubcategories() {
    return this.request("/api/subcategories");
  }

  async getSubcategory(id) {
    return this.request(`/api/subcategories/${id}`);
  }

  // Debug method to check subcategory products
  async debugSubcategoryProducts() {
    return this.request("/api/debug/subcategory-products");
  }

  // Product search methods
  async searchProducts(query, filters = {}) {
    return this.getProducts({ ...filters, search: query });
  }

  async getFeaturedProducts(limit = 8) {
    return this.getProducts({ featured: true, status: "active", limit });
  }

  async getProductsByCategory(category, limit = null) {
    return this.getProducts({ category, status: "active", limit });
  }

  // Inventory methods
  async getInventoryLog(productId) {
    return this.request(`/api/products/${productId}/inventory`);
  }

  async addInventoryLog(productId, logData) {
    return this.request(`/api/products/${productId}/inventory`, {
      method: "POST",
      body: JSON.stringify(logData),
    });
  }

  // Batch operations
  async bulkUpdateProducts(updates) {
    return this.request("/api/products/bulk", {
      method: "PATCH",
      body: JSON.stringify({ updates }),
    });
  }

  async bulkDeleteProducts(ids) {
    return this.request("/api/products/bulk", {
      method: "DELETE",
      body: JSON.stringify({ ids }),
    });
  }

  // Admin API methods
  async adminLogin(email, password) {
    return this.request("/api/admin/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  }

  async getDashboardStats() {
    return this.request("/api/admin/dashboard");
  }

  // Admin Users API methods
  async getAdminUsers() {
    const response = await this.request("/api/admin/users");
    console.log("getAdminUsers response:", response);
    return response;
  }

  async getAdminUser(id) {
    return this.request(`/api/admin/users/${id}`);
  }

  async setupPassword(token, password) {
    console.log(
      "API: Setting up password with token:",
      token ? "present" : "missing",
    );
    return this.request("/api/admin/setup-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    });
  }

  async createAdminUser(userData) {
    return this.request("/api/admin/users", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  async updateAdminUser(id, userData) {
    return this.request(`/api/admin/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    });
  }

  async deleteAdminUser(id) {
    return this.request(`/api/admin/users/${id}`, {
      method: "DELETE",
    });
  }

  // Admin Logs API methods
  async getAdminLogs(filters = {}) {
    const params = new URLSearchParams();

    if (filters.page) params.append("page", filters.page);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.action_type) params.append("action_type", filters.action_type);
    if (filters.resource_type)
      params.append("resource_type", filters.resource_type);
    if (filters.admin_id) params.append("admin_id", filters.admin_id);
    if (filters.start_date) params.append("start_date", filters.start_date);
    if (filters.end_date) params.append("end_date", filters.end_date);

    const queryString = params.toString();
    const endpoint = `/api/admin/logs${queryString ? `?${queryString}` : ""}`;

    return this.request(endpoint);
  }

  async migrateAdminLogs() {
    return this.request("/api/admin/migrate-logs", {
      method: "POST",
    });
  }

  async testAdminLogs() {
    return this.request("/api/admin/test-logs", {
      method: "POST",
    });
  }

  // Additional product methods
  async getProductById(id) {
    // Alias for getProduct for consistency
    return this.getProduct(id);
  }

  // Enhanced mock response for admin users with proper structure
  getAdminUsersMockResponse() {
    return {
      success: true,
      data: [
        {
          id: 1,
          username: "admin",
          firstName: "John",
          lastName: "Admin",
          email: "admin@xplod-group.com",
          role: "super_admin",
          status: "active",
          permissions: {
            canManageProducts: true,
            canManageUsers: true,
            canManageOrders: true,
            canViewAnalytics: true,
            canManageSettings: true,
          },
          createdAt: "2024-01-01T00:00:00Z",
          updatedAt: "2024-08-22T08:30:00Z",
        },
        {
          id: 2,
          username: "manager",
          firstName: "Sarah",
          lastName: "Manager",
          email: "manager@xplod-group.com",
          role: "admin",
          status: "active",
          permissions: {
            canManageProducts: true,
            canManageUsers: false,
            canManageOrders: true,
            canViewAnalytics: true,
            canManageSettings: false,
          },
          createdAt: "2024-02-15T00:00:00Z",
          updatedAt: "2024-08-21T14:20:00Z",
        },
        {
          id: 3,
          username: "support",
          firstName: "Mike",
          lastName: "Support",
          email: "support@xplod-group.com",
          role: "moderator",
          status: "inactive",
          permissions: {
            canManageProducts: false,
            canManageUsers: false,
            canManageOrders: true,
            canViewAnalytics: false,
            canManageSettings: false,
          },
          createdAt: "2024-03-20T00:00:00Z",
          updatedAt: "2024-08-15T10:45:00Z",
        },
      ],
    };
  }

  // Contact API methods
  async submitContact(contactData) {
    return this.request("/api/contact", {
      method: "POST",
      body: JSON.stringify(contactData),
    });
  }
}

// Export singleton instance
export const apiService = new ApiService();

// Named exports for individual methods
export const {
  getProducts,
  getProduct,
  getProductBySlug,
  createProduct,
  updateProduct,
  deleteProduct,
  updateProductStock,
  getCategories,
  getCategory,
  getCategoryBySlug,
  getCategoryCounts,
  getSubcategories,
  getSubcategory,
  debugSubcategoryProducts,
  createCategory, // ADD THIS
  getBrands, // ADD THIS
  getBrand, // ADD THIS
  createBrand, // ADD THIS
  updateBrand, // ADD THIS
  deleteBrand, // ADD THIS
  getTypes,        // ADD THIS
  getType,         // ADD THIS
  createType,      // ADD THIS
  updateType,      // ADD THIS
  deleteType,      // ADD THIS
  searchProducts,
  getFeaturedProducts,
  getProductsByCategory,
  getInventoryLog,
  addInventoryLog,
  bulkUpdateProducts,
  bulkDeleteProducts,
} = apiService;

export default apiService;
